import { Alert, Box, Button, Stack, Typography } from "@mui/material"
import { useSignOut } from "@utils/auth"
import { useNavigate } from "react-router-dom"
import type { CliAuthRequest } from "./constants"
import { clearCliAuthRequest } from "./constants"
import { describeCallbackHost, submitCliAuthHandoff } from "./helpers"

interface CliAuthConsentProps {
	request: CliAuthRequest
	accessToken: string
	email?: string
}

/**
 * Consent screen for the local-tool ("CLI") login flow. Nothing is ever handed back until the user
 * explicitly authorizes here — this is what stops a crafted /cli-auth link from silently shipping a
 * signed-in user's token to a local port.
 */
export function CliAuthConsent({ request, accessToken, email }: CliAuthConsentProps) {
	const navigate = useNavigate()
	const signOut = useSignOut()

	const handleAuthorize = () => submitCliAuthHandoff(request, accessToken)

	const handleDifferentAccount = () => {
		// Keep the pending request in storage: after the new sign-in the user lands back here.
		signOut()
	}

	const handleCancel = () => {
		clearCliAuthRequest()
		navigate("/", { replace: true })
	}

	return (
		<Stack spacing={2}>
			<Typography variant="h5">Authorize sign-in</Typography>
			<Typography>A local tool is requesting access to your session.</Typography>

			<Box>
				<Typography>It will receive a sign-in token at:</Typography>
				<Typography sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
					{describeCallbackHost(request.redirectUri)}
				</Typography>
			</Box>

			<Alert severity="info">Signed in as {email ?? "your current account"}</Alert>

			<Stack spacing={1.5}>
				<Button variant="contained" fullWidth onClick={handleAuthorize} data-testid="cli-auth-authorize">
					Authorize
				</Button>
				<Button variant="outlined" fullWidth onClick={handleDifferentAccount} data-testid="cli-auth-different-account">
					Sign in with a different account
				</Button>
				<Button variant="text" fullWidth onClick={handleCancel} data-testid="cli-auth-cancel">
					Cancel
				</Button>
			</Stack>
		</Stack>
	)
}
